import type React from 'react';
// Small presentational building blocks shared by the pages. Kept in one file
// because each is a handful of lines and they are always imported together.
import { useEffect, useRef, useState, type ReactNode } from 'react';

// --- Avatar -----------------------------------------------------------------

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + second).toUpperCase();
}

export function Avatar({
  src,
  name,
  size = 40,
}: {
  src: string | null;
  name: string;
  size?: number;
}): React.JSX.Element {
  const [failed, setFailed] = useState(false);
  const style = { width: size, height: size, fontSize: Math.round(size / 2.6) };

  if (!src || failed) {
    return (
      <div className="avatar avatar--fallback" style={style} aria-hidden="true">
        {initials(name)}
      </div>
    );
  }
  return (
    <img
      className="avatar"
      style={style}
      src={src}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

// --- Pill -------------------------------------------------------------------

export function Pill({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'accent' | 'danger' | 'success' | 'warn';
}): React.JSX.Element {
  return <span className={`pill pill--${tone}`}>{children}</span>;
}

// --- StatCard ---------------------------------------------------------------

export function StatCard({
  label,
  value,
  hint,
  degraded,
}: {
  label: string;
  value: number | null;
  hint?: string;
  degraded?: boolean;
}): React.JSX.Element {
  return (
    <div className={`stat-card${degraded ? ' stat-card--degraded' : ''}`}>
      <div className="stat-card__label">{label}</div>
      {/* An unavailable metric shows "—", never 0: a fabricated zero on a
          dashboard is worse than an obvious gap. */}
      <div className="stat-card__value">
        {degraded || value === null ? '—' : value.toLocaleString('pt-BR')}
      </div>
      {degraded ? (
        <div className="stat-card__hint stat-card__hint--warn">serviço indisponível</div>
      ) : hint ? (
        <div className="stat-card__hint">{hint}</div>
      ) : null}
    </div>
  );
}

// --- AsyncBoundary ----------------------------------------------------------

export function AsyncBoundary({
  loading,
  error,
  onRetry,
  children,
  empty,
}: {
  loading: boolean;
  error: Error | null;
  onRetry?: () => void;
  children: ReactNode;
  empty?: boolean;
}): React.JSX.Element {
  if (error) {
    return (
      <div className="empty-state empty-state--error">
        <p>{error.message}</p>
        {onRetry ? (
          <button type="button" className="btn" onClick={onRetry}>
            Tentar novamente
          </button>
        ) : null}
      </div>
    );
  }
  if (loading) return <div className="page-loading">Carregando…</div>;
  if (empty) return <div className="empty-state">Nada por aqui.</div>;
  return <>{children}</>;
}

// --- Modal ------------------------------------------------------------------

export function Modal({
  title,
  onClose,
  children,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}): React.JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        className={`modal${wide ? ' modal--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="modal__header">
          <h2>{title}</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </header>
        <div className="modal__body">{children}</div>
      </div>
    </div>
  );
}

// --- ConfirmDialog ----------------------------------------------------------

/**
 * Destructive confirmation. When `confirmPhrase` is given, the action stays
 * disabled until the operator types it exactly — deliberate friction for things
 * like deleting a bot or an admin.
 */
export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirmar',
  confirmPhrase,
  busy,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  confirmPhrase?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  const [typed, setTyped] = useState('');
  const ready = !confirmPhrase || typed === confirmPhrase;

  return (
    <Modal title={title} onClose={onCancel}>
      <div className="confirm">
        <div className="confirm__message">{message}</div>
        {confirmPhrase ? (
          <label className="field">
            <span>
              Digite <code>{confirmPhrase}</code> para confirmar
            </span>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              autoFocus
            />
          </label>
        ) : null}
        <div className="modal__actions">
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--danger"
            onClick={onConfirm}
            disabled={!ready || busy}
          >
            {busy ? 'Aguarde…' : confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// --- SecretReveal -----------------------------------------------------------

/** Shows a generated password exactly once, with a copy button. */
export function SecretReveal({ secret }: { secret: string }): React.JSX.Element {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      timer.current = window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="secret">
      <p className="secret__warning">
        Copie agora — esta senha <strong>não será exibida novamente</strong>.
      </p>
      <div className="secret__row">
        <code className="secret__value">{secret}</code>
        <button type="button" className="btn" onClick={() => void copy()}>
          {copied ? 'Copiado!' : 'Copiar'}
        </button>
      </div>
    </div>
  );
}

// --- Toast ------------------------------------------------------------------

export function Toast({
  message,
  tone = 'success',
  onDone,
}: {
  message: string;
  tone?: 'success' | 'error';
  onDone: () => void;
}): React.JSX.Element {
  useEffect(() => {
    const id = window.setTimeout(onDone, 4000);
    return () => window.clearTimeout(id);
  }, [onDone]);

  return <div className={`toast toast--${tone}`}>{message}</div>;
}

// --- relative time ----------------------------------------------------------

const RTF = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' });
const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 3600_000],
  ['month', 30 * 24 * 3600_000],
  ['day', 24 * 3600_000],
  ['hour', 3600_000],
  ['minute', 60_000],
];

/** "há 3 horas" without pulling in a date library. */
export function relativeTime(iso: string | null): string {
  if (!iso) return 'nunca';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diff = then - Date.now();
  const abs = Math.abs(diff);
  for (const [unit, ms] of UNITS) {
    if (abs >= ms) return RTF.format(Math.round(diff / ms), unit);
  }
  return 'agora mesmo';
}
